import {
  format,
  parseISO,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInQuarters,
  differenceInYears,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { TransactionStoreModel } from "../../store/transaction.store.model";
import { ModelView } from "../model.view";
import { TransactionView } from "./transaction.view";
import {
  GlossQuantity,
  GlossQuantityUtils,
} from "@bitwarden/web-vault/app/models/data/shared/gloss-quantity";
import { ValuationUtilities } from "@bitwarden/web-vault/app/models/data/valuation.data";
import { AccountTransactionsMap } from "@bitwarden/web-vault/app/models/types/revaluation.types";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { GlossNumberUtils } from "@bitwarden/web-vault/app/models/data/shared/gloss-number";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { AllocationFactory } from "@bitwarden/web-vault/app/models/store/allocations/allocation.factory";
import { Allocation } from "@bitwarden/web-vault/app/models/store/allocations/allocation.class";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import {
  GranularityProperty,
  dayGranularity,
  monthGranularity,
  quarterGranularity,
  weekGranularity,
  yearGranularity,
} from "@bitwarden/web-vault/app/models/types/balanceGroupingTypes";
import { DEFAULT_CATEGORY_ID } from "@bitwarden/web-vault/app/models/constants/category.constants";
import { DEFAULT_CLASSIFICATION_ID } from "@bitwarden/web-vault/app/models/constants/classification.constants";

export type TransactionViewProperties = Omit<
  TransactionView,
  keyof ModelView<TransactionStoreModel> | "constructor" | "toStoreModel" | "clone"
>;

export function getTransactionViewOf(
  transactionViewJson: TransactionViewProperties,
): TransactionView {
  /* debugger;*/
  transactionViewJson.quantity = GlossQuantityUtils.fromSelfProperties(
    transactionViewJson.quantity,
  );
  transactionViewJson.c_valuation = ValuationUtilities.fromSelfProperties(
    transactionViewJson.c_valuation,
  );
  transactionViewJson.glossPostDate = GlossDateFactory.createFromAnyObject(
    transactionViewJson.glossPostDate,
  );
  transactionViewJson.allocations = AllocationFactory.createFromAnyObjectArray(
    transactionViewJson.allocations,
  );

  const transactionView = new TransactionView();
  Object.assign(transactionView, transactionViewJson);
  return transactionView;
}

/**
 * `glossPostDate.date` is stored as a UTC ISO instant. Returns the UTC time-of-day as
 * `HH:mm:ss.SSS` for use with {@link GlossDateAbstraction.getEnrichedDate} (same digits as between `T` and `Z`).
 */
export function utcWallClockTimeFromGlossPostDateIso(
  iso: string | null | undefined,
): string | undefined {
  if (!iso || typeof iso !== "string") {
    return undefined;
  }
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) {
    return undefined;
  }
  return formatInTimeZone(d, "UTC", "HH:mm:ss.SSS");
}

export function setAccountTransactionsMap(transactions: TransactionView[]): AccountTransactionsMap {
  return transactions.reduce((accMap, transaction) => {
    if (accMap.has(transaction.accountLink.id)) {
      accMap.get(transaction.accountLink.id).push(transaction);
    } else {
      accMap.set(transaction.accountLink.id, [transaction]);
    }
    return accMap;
  }, new Map<string, TransactionView[]>());
}

export function createTransactionView(
  accountID: string,
  institutionID: string,
  symbolAmount: number,
  symbol: string,
  transactionDate: Date,
  description: string,
  allocations: Allocation[],
  linkedTo: Array<string> = [],
  keepTransactionTime = false,
  definition: TransactionStatusEnum = TransactionStatusEnum.fake,
  balance?: number,
  currency?: string,
): TransactionView {
  const transactionView = new TransactionView();

  const direction = symbolAmount >= 0 ? TransactionDirection.In : TransactionDirection.Out;

  const actualQuantity = GlossNumberUtils.fromSelfProperties({
    amount: Math.abs(symbolAmount),
    symbol: symbol,
    precision: 8,
  });

  const quantity = GlossQuantityUtils.fromSelfProperties({
    actualQuantity,
    currency: currency ?? symbol,
    convrate: 1,
    convsym: "",
  } as GlossQuantity);

  transactionView.accountLink = { id: accountID };
  transactionView.c_institutionLink = { id: institutionID };

  if (allocations && allocations.length > 0) {
    transactionView.allocations = allocations;
  } else {
    transactionView.allocations = AllocationUtils.createDefaultAllocations(symbolAmount);
  }
  transactionView.description = description;
  transactionView.direction = direction;
  transactionView.quantity = quantity;
  if (keepTransactionTime) {
    const time = format(transactionDate, "HH:mm:ss");
    transactionView.glossPostDate = GlossDateFactory.createFromDate(
      transactionDate,
      keepTransactionTime,
      time,
    );
  } else {
    transactionView.glossPostDate = GlossDateFactory.createFromDate(transactionDate);
  }
  transactionView.definition = definition;
  transactionView.linkedTo = linkedTo;
  transactionView.origin = "manual";

  if (balance !== null && balance !== undefined) {
    transactionView.bankImportedBalance = balance;
  }
  return transactionView;
}

export function enrichTransactionDate(transaction: TransactionView): void {
  const timeFromIso = utcWallClockTimeFromGlossPostDateIso(transaction.glossPostDate?.date);
  if (transaction.definition === TransactionStatusEnum.closing) {
    transaction.postDate = timeFromIso
      ? transaction.glossPostDate.getEnrichedDate("closing", timeFromIso)
      : transaction.glossPostDate.getEnrichedDate("closing");
  } else if (transaction.definition === TransactionStatusEnum.opening) {
    transaction.postDate = transaction.glossPostDate.getEnrichedDate("opening");
  } else if (transaction.definition === TransactionStatusEnum.starting_alignment) {
    transaction.postDate = transaction.glossPostDate.getEnrichedDate("opening");
  } else {
    transaction.postDate = timeFromIso
      ? transaction.glossPostDate.getEnrichedDate(null, timeFromIso)
      : transaction.glossPostDate.getEnrichedDate();
  }
}

export function enrichAccountData(
  account: AccountView,
  institutionView: InstitutionView,
  transaction: TransactionView,
): void {
  transaction.c_institutionLink = { id: account.institutionLink.institutionId };
  transaction.c_country.id = institutionView.swift.countryCode;
}

export function enrichAllocations(transaction: TransactionView): void {
  transaction.c_categories = Array.from(
    new Set(transaction.allocations.map((alloc) => alloc.categoryLink.id)),
  );
  transaction.c_classifications = Array.from(
    new Set(transaction.allocations.map((alloc) => alloc.classificationLink.id)),
  );

  transaction.c_allocationsNested = AllocationUtils.getNestedAllocations(transaction.allocations);

  // set the normalized allocations on the transaction if the valuation price has already been set
  AllocationUtils.createNormalizedAllocationsOnTransaction(transaction);
}

/**
 * Enriches the transaction with derived properties such as formatted date and classifications/categories arrays
 * as well at normalising the quantity into the valuation price and normalising any values in the allocations
 * @param transaction
 */
export function enrichTransaction(transaction: TransactionView): void {
  enrichTransactionDate(transaction);
  enrichAllocations(transaction);
}

export function countPeriods(
  startDate: Date,
  endDate: Date,
  granularity: GranularityProperty,
): number {
  if (endDate < startDate) {
    return 0;
  }
  switch (granularity) {
    case dayGranularity:
      return differenceInDays(endDate, startDate);
    case weekGranularity:
      return differenceInWeeks(endDate, startDate);
    case monthGranularity:
      return differenceInMonths(endDate, startDate);
    case quarterGranularity:
      return differenceInQuarters(endDate, startDate);
    case yearGranularity:
      return differenceInYears(endDate, startDate);
    default:
      return 0;
  }
}

export function newAllocationWithNewCategory(
  newCategoryid?: string,
  allocationAmount?: number,
): Allocation[] {
  // in the current solution we're always using default classification
  return [
    AllocationFactory.createAllocation(
      newCategoryid ? newCategoryid : DEFAULT_CATEGORY_ID,
      DEFAULT_CLASSIFICATION_ID,
      allocationAmount,
    ),
  ];
}
